import { useModelPlans } from '../api/modelPlans.ts';
import { useT } from '../i18n/index.ts';
import { FormField, Select } from '../ui/index.ts';

/** Radix Select forbids empty-string values; map this sentinel back to null. */
export const MODEL_PLAN_NONE_VALUE = '__none';

export function planSelectValue(planId: string | null): string {
  return planId === null || planId.length === 0 ? MODEL_PLAN_NONE_VALUE : planId;
}

export function planIdFromSelect(value: string): string | null {
  return value === MODEL_PLAN_NONE_VALUE ? null : value;
}

/**
 * Active-plan picker used by mint, provision, rebind, and org assign.
 * Loads via GET /v1/ops/model-plans?status=active (plus brand for admin).
 */
export function ModelPlanSelect({
  brand,
  value,
  onChange,
  allowNone,
  disabled = false,
  fieldId,
  label,
  hint,
  testId,
}: {
  readonly brand: string | undefined;
  readonly value: string | null;
  readonly onChange: (planId: string | null) => void;
  readonly allowNone: boolean;
  readonly disabled?: boolean;
  readonly fieldId: string;
  readonly label: string;
  readonly hint?: string;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const query = useModelPlans({ status: 'active', brand }, true);
  const plans = query.data?.model_plans ?? [];
  const options = [
    ...(allowNone
      ? [{ value: MODEL_PLAN_NONE_VALUE, label: t('model_plans.select.none') }]
      : []),
    ...plans.map((plan) => ({ value: plan.id, label: plan.name })),
  ];
  const empty = !query.isLoading && plans.length === 0;
  const hintText = query.isError
    ? t('model_plans.select.load_error')
    : empty
      ? t('model_plans.select.empty')
      : hint;

  return (
    <FormField label={label} fieldId={fieldId} hint={hintText}>
      <Select
        id={fieldId}
        value={planSelectValue(value)}
        onValueChange={(next) => onChange(planIdFromSelect(next))}
        options={options}
        placeholder={t('model_plans.select.placeholder')}
        disabled={disabled || query.isLoading || (!allowNone && empty)}
        ariaLabel={label}
        testId={testId}
      />
    </FormField>
  );
}
